import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  useId,
  type RefObject,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useClickOutsideWithGlobal } from '../../../shared/hooks/useClickOutside';
import { useEscapeKey } from '../../../shared/hooks/useEscapeKey';
import type { PopupArrowMode, PopupArrowPlacement } from '@/shared/types/popup';
import {
  clampClientPointForPopupAnchor,
  computePopupLayout,
  type PopupPositionProp,
} from '../utils/popupPosition';
import { createPopupHoverCloseScheduler } from './createPopupHoverCloseScheduler';
import { getOpenOnHoverDelayMs, isOpenOnHoverEnabled } from './popupHoverConstants';
import { closeOtherPopups, trackPopupClose, trackPopupOpen } from './popupGlobalRegistry';
import { usePopupCloseEventSubscription } from './usePopupCloseEventSubscription';
import { usePopupExternalTrigger } from './usePopupExternalTrigger';
import { usePopupPositioning } from './usePopupPositioning';

/** Re-export para imports profundos que apuntaban a este fichero. */
export {
  POPUP_OPEN_ON_HOVER_CLOSE_DELAY_MS,
  POPUP_OPEN_ON_HOVER_DEFAULT_MS,
} from './popupHoverConstants';

interface UsePopupProps {
  position?:
    | 'top'
    | 'top-left'
    | 'top-right'
    | 'bottom'
    | 'bottom-center'
    | 'bottom-left'
    | 'bottom-right'
    | 'left'
    | 'right';
  offset?: number;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  popupId?: string;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  absolute?: boolean;
  popoverArrow?: PopupArrowMode;
  /** `true` usa retardo por defecto; número = ms hasta abrir. */
  openOnHover?: boolean | number;
  /** Ver {@link PopupProps.hoverAnchorFollowPointer}. */
  hoverAnchorFollowPointer?: boolean;
  /** Ver {@link PopupProps.openOnHoverAllowPanelPointer}. */
  openOnHoverAllowPanelPointer?: boolean;
  externalTriggerRef?: RefObject<HTMLElement | null>;
}

/**
 * Estado y efectos para un popup anclado a un trigger: posición, cierre por fuera/escape,
 * coordinación de un solo popup abierto y modo hover (incl. coexistencia con modales del DS).
 *
 * @returns Refs al trigger/panel/contenido, estilos calculados y handlers para el organismo `Popup`.
 */
export const usePopup = ({
  position = 'bottom-right',
  offset = 4,
  closeOnClickOutside = true,
  closeOnEscape = true,
  popupId,
  onOpen,
  onClose,
  disabled = false,
  absolute = false,
  popoverArrow = 'auto',
  openOnHover,
  hoverAnchorFollowPointer = false,
  openOnHoverAllowPanelPointer = true,
  externalTriggerRef,
}: UsePopupProps) => {
  const uniqueId = useId();
  const popupUniqueId = popupId || `popup-${uniqueId}`;

  const [isOpen, setIsOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});
  const [isPositioned, setIsPositioned] = useState(false);
  const [arrowPlacement, setArrowPlacement] = useState<PopupArrowPlacement | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const hoverOpenEnabled = isOpenOnHoverEnabled(openOnHover);
  const hoverOpenDelayMs = hoverOpenEnabled
    ? getOpenOnHoverDelayMs(openOnHover as true | number)
    : 0;

  const trackHoverPointer = hoverOpenEnabled && hoverAnchorFollowPointer;
  const hoverPointerClientRef = useRef<{ x: number; y: number } | null>(null);
  const pointerRepositionRafRef = useRef<number | null>(null);

  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const overTriggerRef = useRef(false);
  const overPopupRef = useRef(false);
  const isOpenRef = useRef(false);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const clearOpenHoverTimer = useCallback(() => {
    if (openTimerRef.current !== null) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const clearCloseHoverTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const cancelPointerRepositionRaf = useCallback(() => {
    if (pointerRepositionRafRef.current !== null) {
      cancelAnimationFrame(pointerRepositionRafRef.current);
      pointerRepositionRafRef.current = null;
    }
  }, []);

  const extraInsideRefsForClickOutside = useMemo(
    () => (externalTriggerRef ? [externalTriggerRef] : undefined),
    [externalTriggerRef]
  );

  const calculatePosition = useCallback(() => {
    const triggerElement = externalTriggerRef?.current ?? triggerRef.current;
    const measureElement = contentRef.current;

    if (!triggerElement || !measureElement) return;

    const popupRect = measureElement.getBoundingClientRect();

    if (popupRect.width === 0 || popupRect.height === 0) {
      return;
    }

    const pos = (position ?? 'bottom-right') as PopupPositionProp;

    const fullRect = triggerElement.getBoundingClientRect();
    const pointer = hoverPointerClientRef.current;
    const usePointer = trackHoverPointer && pointer !== undefined && pointer !== null;

    const triggerRect = usePointer
      ? ({
          top: pointer.y,
          left: pointer.x,
          width: 1,
          height: 1,
          bottom: pointer.y + 1,
          right: pointer.x + 1,
          x: pointer.x,
          y: pointer.y,
          toJSON: () => ({}),
        } as DOMRectReadOnly)
      : fullRect;

    const triggerOffsetBox = usePointer
      ? {
          top: triggerElement.offsetTop + (pointer.y - fullRect.top),
          left: triggerElement.offsetLeft + (pointer.x - fullRect.left),
          width: 1,
          height: 1,
        }
      : {
          top: triggerElement.offsetTop,
          left: triggerElement.offsetLeft,
          width: triggerElement.offsetWidth,
          height: triggerElement.offsetHeight,
        };

    const { popupStyle: nextStyle, arrowPlacement: nextPlacement } = computePopupLayout({
      position: pos,
      offset,
      absolute,
      popoverArrow,
      popupUniqueId,
      triggerRect,
      popupRect,
      triggerOffsetBox,
    });

    setPopupStyle(nextStyle);
    setArrowPlacement(nextPlacement);
    setIsPositioned(true);
  }, [
    position,
    offset,
    popupUniqueId,
    absolute,
    popoverArrow,
    externalTriggerRef,
    trackHoverPointer,
  ]);

  const scheduleRecalculateFromPointerMove = useCallback(() => {
    if (pointerRepositionRafRef.current !== null) {
      return;
    }
    pointerRepositionRafRef.current = requestAnimationFrame(() => {
      pointerRepositionRafRef.current = null;
      calculatePosition();
    });
  }, [calculatePosition]);

  const closePopup = useCallback(() => {
    clearOpenHoverTimer();
    clearCloseHoverTimer();
    cancelPointerRepositionRaf();
    hoverPointerClientRef.current = null;
    overTriggerRef.current = false;
    overPopupRef.current = false;
    setIsOpen(false);
    setIsPositioned(false);
    setArrowPlacement(null);
    trackPopupClose(popupUniqueId);
    onClose?.();
  }, [
    popupUniqueId,
    onClose,
    clearOpenHoverTimer,
    clearCloseHoverTimer,
    cancelPointerRepositionRaf,
  ]);

  const openPopup = useCallback(() => {
    if (disabled || isOpen) {
      return;
    }
    closeOtherPopups(popupUniqueId);
    setPopupStyle({});
    setIsPositioned(false);
    setArrowPlacement(null);
    setIsOpen(true);
    trackPopupOpen(popupUniqueId);
    onOpen?.();
  }, [disabled, isOpen, popupUniqueId, onOpen]);

  const scheduleHoverClose = useMemo(
    () =>
      createPopupHoverCloseScheduler({
        isOpenRef,
        overTriggerRef,
        overPopupRef,
        closeTimerRef,
        clearCloseHoverTimer,
        closePopup,
      }),
    [clearCloseHoverTimer, closePopup]
  );

  const onHoverTriggerEnter = useCallback(
    (event?: MouseEvent | ReactMouseEvent<Element>) => {
      if (!hoverOpenEnabled || disabled) {
        return;
      }
      const triggerElement = externalTriggerRef?.current ?? triggerRef.current;
      if (trackHoverPointer && triggerElement && event && 'clientX' in event) {
        const p = clampClientPointForPopupAnchor(
          triggerElement.getBoundingClientRect(),
          event.clientX,
          event.clientY
        );
        hoverPointerClientRef.current = p;
      }
      overTriggerRef.current = true;
      clearCloseHoverTimer();
      clearOpenHoverTimer();
      openTimerRef.current = window.setTimeout(() => {
        openTimerRef.current = null;
        if (overTriggerRef.current && !disabled) {
          openPopup();
        }
      }, hoverOpenDelayMs);
    },
    [
      hoverOpenEnabled,
      disabled,
      hoverOpenDelayMs,
      clearCloseHoverTimer,
      clearOpenHoverTimer,
      openPopup,
      trackHoverPointer,
      externalTriggerRef,
    ]
  );

  const onHoverTriggerLeave = useCallback(() => {
    if (!hoverOpenEnabled) {
      return;
    }
    overTriggerRef.current = false;
    clearOpenHoverTimer();
    if (isOpenRef.current) {
      scheduleHoverClose();
    }
  }, [hoverOpenEnabled, clearOpenHoverTimer, scheduleHoverClose]);

  const onHoverTriggerPointerMove = useCallback(
    (e: ReactMouseEvent) => {
      if (!trackHoverPointer) {
        return;
      }
      const triggerElement = externalTriggerRef?.current ?? triggerRef.current;
      if (!triggerElement) {
        return;
      }
      hoverPointerClientRef.current = clampClientPointForPopupAnchor(
        triggerElement.getBoundingClientRect(),
        e.clientX,
        e.clientY
      );
      if (isOpenRef.current) {
        scheduleRecalculateFromPointerMove();
      }
    },
    [trackHoverPointer, externalTriggerRef, scheduleRecalculateFromPointerMove]
  );

  const onHoverTriggerPointerMoveNative = useCallback(
    (e: MouseEvent) => {
      if (!trackHoverPointer) {
        return;
      }
      const triggerElement = externalTriggerRef?.current ?? triggerRef.current;
      if (!triggerElement) {
        return;
      }
      hoverPointerClientRef.current = clampClientPointForPopupAnchor(
        triggerElement.getBoundingClientRect(),
        e.clientX,
        e.clientY
      );
      if (isOpenRef.current) {
        scheduleRecalculateFromPointerMove();
      }
    },
    [trackHoverPointer, externalTriggerRef, scheduleRecalculateFromPointerMove]
  );

  const onHoverPopupEnter = useCallback(() => {
    if (!hoverOpenEnabled) {
      return;
    }
    overPopupRef.current = true;
    clearCloseHoverTimer();
  }, [hoverOpenEnabled, clearCloseHoverTimer]);

  const onHoverPopupLeave = useCallback(() => {
    if (!hoverOpenEnabled) {
      return;
    }
    overPopupRef.current = false;
    scheduleHoverClose();
  }, [hoverOpenEnabled, scheduleHoverClose]);

  useEffect(() => {
    return () => {
      clearOpenHoverTimer();
      clearCloseHoverTimer();
      cancelPointerRepositionRaf();
    };
  }, [clearOpenHoverTimer, clearCloseHoverTimer, cancelPointerRepositionRaf]);

  usePopupCloseEventSubscription({ popupUniqueId, closePopup });

  const togglePopup = useCallback(
    (event?: ReactMouseEvent) => {
      if (event) {
        event.stopPropagation();
      }

      if (disabled) return;

      if (!isOpen) {
        openPopup();
      } else {
        closePopup();
      }
    },
    [disabled, isOpen, openPopup, closePopup]
  );

  usePopupExternalTrigger({
    externalTriggerRef,
    disabled,
    hoverOpenEnabled,
    hoverAnchorFollowPointer: trackHoverPointer,
    togglePopup,
    onHoverTriggerEnter,
    onHoverTriggerLeave,
    onHoverTriggerPointerMoveNative,
  });

  usePopupPositioning({
    isOpen,
    isPositioned,
    absolute,
    contentRef,
    triggerRef,
    externalTriggerRef,
    calculatePosition,
  });

  useClickOutsideWithGlobal({
    isActive: isOpen,
    disabled: disabled,
    elementRef: popupRef,
    containerRef: containerRef,
    extraInsideRefs: extraInsideRefsForClickOutside,
    elementId: popupUniqueId,
    elementType: 'popup',
    closeOnClickOutside,
    onOutsideClick: closePopup,
  });

  useEscapeKey(isOpen, closePopup, !closeOnEscape);

  const openOnHoverTriggerProps = hoverOpenEnabled
    ? {
        onMouseEnter: onHoverTriggerEnter,
        onMouseLeave: onHoverTriggerLeave,
        ...(trackHoverPointer ? { onMouseMove: onHoverTriggerPointerMove } : {}),
      }
    : undefined;

  const openOnHoverPopupProps =
    hoverOpenEnabled && openOnHoverAllowPanelPointer
      ? { onMouseEnter: onHoverPopupEnter, onMouseLeave: onHoverPopupLeave }
      : undefined;

  return {
    isOpen,
    popupStyle,
    isPositioned,
    triggerRef,
    popupRef,
    contentRef,
    arrowPlacement,
    containerRef,
    popupUniqueId,
    togglePopup,
    closePopup,
    openPopup,
    openOnHoverTriggerProps,
    openOnHoverPopupProps,
  };
};
